<template>
  <div>
    <div v-if="isPublicOrMember">
      <div v-if="projectId && !projectLoading" class="">
        <div class="card-body">
          <div class="row mb-3">
            <h3 class="mb-0 col-md-3">Datasheets</h3>
            <div class="input-group col-md-6">
              <b-input-group-prepend>
                <span
                  class="btn btn-secondary"
                  title="Search by datasheet name"
                >
                  <i v-if="!isBusy" class="fas fa-search"></i>
                  <i v-if="isBusy" class="fas fa-spinner fa-spin"></i>
                </span>
              </b-input-group-prepend>
              <b-form-input
                placeholder="Search by Datasheet Name"
                name="search"
                type="search"
                v-model="searchText"
                debounce="1000"
              ></b-form-input>
              <div class="input-group-append" v-if="searchText">
                <b-button @click="searchText = ''"
                ><i class="fas fa-times-circle"></i
                ></b-button>
              </div>
            </div>
            <b-col>
              <div
                class="flex-shrink-1 text-right"
                v-if="isManager">
                <button class="btn btn-primary" @click="onCreateDatasheet">
                  <i class="fa fa-plus text-white"></i>
                  New Datasheet
                </button>
              </div>
            </b-col>
          </div>
          <div class="row justify-content-between">
            <div class="my-1 col-md-4">
              Showing {{ fromItem }} to {{ toItem }} of
              {{ totalItems }} datasheet<span v-if="totalItems === 0 || totalItems > 1">s</span>
            </div>

            <div class="col-md-3 my-1">
              <b-pagination
                v-if="totalItems > 0"
                :total-rows="totalItems"
                :per-page="perPage"
                v-model="currentPage"
                class="my-0 pagination-holder"
                prev-text="Prev"
                next-text="Next"
                align="fill"
              />
            </div>

            <div class="col-md-3 my-1">
              <b-form-group
                label="Per Page"
                label-align="right"
                label-cols-sm="7"
                label-cols-md="8"
                label-for="perPageSelect"
                class="mb-0"
              >
                <b-form-select
                  v-model="perPage"
                  id="perPageSelect"
                  size="sm"
                  :options="pageOptions"
                  align="right"
                >
                </b-form-select>
              </b-form-group>
            </div>
          </div>
        </div>

        <b-table
          show-empty
          stacked="md"
          id="datasheetsTable"
          ref="datasheetsTable"
          :filter="filterVal"
          :busy="isBusy"
          :items="datasheets"
          :fields="tableFields"
          :current-page="currentPage"
          :per-page="perPage"
          sort-icon-left
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
        >
          <template v-slot:cell(name)="row">
            <span v-if="row.item.name" class="font-weight-bold">
              {{ row.item.name }}
            </span>
            <span v-else class="text-danger">
              Unnamed Datasheet
            </span>
          </template>
          <template v-slot:cell(status)="row">
            <span v-if="!row.item.published" class="text-danger">
              Draft
            </span>
            <span v-else>
              Published
            </span>
          </template>
          <template v-slot:cell(updatedAt)="row">
            <DateTime :date="row.item.updatedAt" :dateTime="false"></DateTime>
          </template>
          <template v-slot:cell(createdAt)="row">
            <DateTime :date="row.item.createdAt" :dateTime="false"></DateTime>
          </template>
          <template v-slot:cell(options)="row">
            <div class="text-right">
              <b-btn v-if="isMember && row.item.published"
                     class="my-1 mr-1 text-white bg-tertiary"
                     :to="{ name: 'DatasheetView', params: { id: row.item.id } }"
                     alt="Add Observation"
                     title="Add Observation">
                <i class="fa fa-plus text-white"></i>
                Add Observation
              </b-btn>
              <b-btn
                class="my-1 mr-1 text-white bg-tertiary"
                :to="{ name: 'DatasheetCreator', params: { id: row.item.id } }"
                alt="Edit Datasheet"
                title="Edit Datasheet"
                v-if="isManager">
                <font-awesome-icon icon="pen"/>
                Edit
              </b-btn>
            </div>
          </template>
          <template #table-busy>
            <VueLoader style="height: 200px" text="Loading Datasheets..."/>
          </template>
        </b-table>
        <div class="card-footer">
          <div class="row justify-content-between">
            <div class="my-1 col-md-4">
              Showing {{ fromItem }} to {{ toItem }} of
              {{ totalItems }} datasheet<span v-if="totalItems === 0 || totalItems > 1">s</span>
            </div>

            <div class="col-md-3 my-1">
              <b-pagination
                v-if="totalItems > 0"
                :total-rows="totalItems"
                :per-page="perPage"
                v-model="currentPage"
                class="my-0 pagination-holder"
                prev-text="Prev"
                next-text="Next"
                align="fill"
              />
            </div>

            <div class="col-md-3 my-1">
              <b-form-group
                label="Per Page"
                label-align="right"
                label-cols-sm="7"
                label-cols-md="8"
                label-for="perPageSelect"
                class="mb-0"
              >
                <b-form-select
                  v-model="perPage"
                  id="perPageSelect"
                  size="sm"
                  :options="pageOptions"
                  align="right"
                >
                </b-form-select>
              </b-form-group>
            </div>
          </div>
        </div>

      </div>
    </div>
    <RequiresMember v-else page="Datasheets"></RequiresMember>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import VueLoader from "../../../layout/Loader";
import RequiresMember from "../tabpermission/requiresMember.vue";
import DateTime from "../../../../components/common/date-time/DateTime.vue";
import {datasheetCreated} from "../../../../store/modules/project/show/getters";

export default {
  components: {
    RequiresMember,
    VueLoader,
    DateTime
  },
  data() {
    return {
      pageOptions: [30, 50, 100, 250, 500],
      isBusy: false,
      tableFields: [
        {
          key: "name",
          label: "Name",
          sortable: true
        },
        {
          key: "status",
          label: "Status",
          sortable: true
        },
        {
          key: "updatedAt",
          label: "Date Last Modified",
          sortable: true,
        },
        {
          key: "createdAt",
          label: "Created Date",
          sortable: true,
        },

        {
          key: "options",
          label: "Options",
          sortable: false,
          class: "text-right",
          thStyle: {
            minWidth: "200px!important"
          }
        }
      ],
    };
  },
  watch: {
    datasheetCreated(datasheet) {
      if (!datasheet) {
        return;
      }
      this.$router.push({
        name: "DatasheetCreator",
        params: {id: datasheet.id}
      });
    }
  },
  computed: {
    ...mapGetters({
      projectId: "project/get/id",
      projectLoading: "project/get/isLoading",
      isPublicOrMember: "project/get/isPublicOrMember",
      isManager: "project/get/isManager",
      isMember: "project/get/isMember",

      datasheetCreated: "project/show/datasheetCreated",

      datasheetPage: "project/get/datasheetPage",
      datasheetItems: "project/get/datasheetItems",
      datasheetTotal: "project/get/datasheetTotal",
      datasheetSearch: "project/get/datasheetSearch",
      datasheetSortBy: "project/get/datasheetSortBy",
      datasheetSortDirection: "project/get/datasheetSortDirection",
      datasheetSortDesc: "project/get/datasheetSortDesc",
    }),
    currentPage: {
      get() {
        return this.datasheetPage
      },
      set(newValue) {
        this.setDatasheetTablePage(newValue)
      }
    },
    totalItems: {
      get() {
        return this.datasheetTotal
      },
      set(newValue) {
        this.setDatasheetTableTotal(newValue)
      }
    },
    perPage: {
      get() {
        return this.datasheetItems
      },
      set(newValue) {
        this.setDatasheetTableItems(newValue)
      }
    },
    searchText: {
      get() {
        return this.datasheetSearch
      },
      set(newValue) {
        this.setDatasheetTableSearch(newValue)
      }
    },
    sortBy: {
      get() {
        return this.datasheetSortBy
      },
      set(newValue) {
        this.setDatasheetSortBy(newValue)
      }
    },
    sortDirection: {
      get() {
        return this.datasheetSortDirection
      },
      set(newValue) {
        this.setDatasheetSortDirection(newValue)
      }
    },
    sortDesc: {
      get() {
        return this.datasheetSortDesc
      },
      set(newValue) {
        this.setDatasheetSortDesc(newValue)
      }
    },

    fromItem() {
      return this.totalItems > 0
        ? (this.currentPage - 1) * this.perPage + 1
        : 0;
    },
    toItem() {
      return this.totalItems < this.currentPage * this.perPage
        ? this.totalItems
        : this.currentPage * this.perPage;
    },

    filterVal: {
      get: function () {
        // This is our multi-filter computed value
        // - search datasheets by name
        //More filters can be added here
        // - also add an equivalent to the store

        let retVal = ''
        if (this.searchText) {
          retVal = retVal.length === 0 ? 'name=' + this.searchText : retVal + '&name=' + this.searchText
        }
        return retVal
      }
    },
    newDatasheet() {
      return {
        name: "",
        instructions: "",
        project: "projects/" + this.projectId,
        projection: "geographic",
        dateFormat: "start_end_minutes",
        published: false
      };
    },
  },
  methods: {
    ...mapActions({
      createDatasheet: "project/show/createDatasheet",
      fetchDatasheets: "project/datasheets/get",

      setDatasheetTablePage: "project/get/setDatasheetTablePage",
      setDatasheetTableItems: "project/get/setDatasheetTableItems",
      setDatasheetTableTotal: "project/get/setDatasheetTableTotal",
      setDatasheetTableSearch: "project/get/setDatasheetTableSearch",
      setDatasheetSortBy: "project/get/setDatasheetTableSortBy",
      setDatasheetSortDirection: "project/get/setDatasheetTableSortDirection",
      setDatasheetSortDesc: "project/get/setDatasheetTableSortDesc",
    }),
    datasheets(ctx) {
      this.isBusy = true;
      let urlParams = null;
      // Handle sorting by status column
      if (ctx.sortBy === 'status') {
        ctx.sortBy = 'published';
      }
      if (this.isManager) {
        urlParams = `${this.urlBuilder(ctx)}`;
      } else {
        urlParams = `${this.urlBuilder(ctx)}&published=true`;
      }
      const data = {id: this.projectId, urlParams};
      return this.fetchDatasheets(data).then(data => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = ctx.sortBy === 'published' ? 'status' : ctx.sortBy || "";
        this.isBusy = false;
        return data["hydra:member"].map(datasheet => {
          return {
            ...datasheet
          };
        });
      });
    },
    onCreateDatasheet() {
      this.createDatasheet(this.newDatasheet);
    }
  }
}
</script>

<style lang="scss" scoped></style>
